<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import Button from '../../../../../components-next/button/Button.vue';

const emit = defineEmits(['update', 'validation', 'show-info']);
const store = useStore();

const props = defineProps({
  initialFields: {
    type: Array,
    default: () => ([]) // Inicia vazio, campos são opcionais
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  fieldType: {
    type: String,
    required: true,
    validator: (value) => ['tag', 'team'].includes(value)
  },
  childComponent: {
    type: Object,
    required: true
  },
  storeGetter: {
    type: String,
    required: true
  },
  storeAction: {
    type: String,
    required: true
  },
  excludedTitles: {
    type: Array,
    default: () => []
  },
  addButtonLabel: {
    type: String,
    required: true
  }
});

const fields = ref([...props.initialFields]);
const fieldsValid = ref(true); // Inicializa como true pois campos são opcionais

// Buscar dados disponíveis do store
onMounted(() => {
  store.dispatch(props.storeAction);
});

// Observar mudanças e emitir atualizações para o componente pai
watch(fields, (newValue) => {
  emit('update', [...newValue]);
}, { deep: true });

// Quando as props iniciais mudam, atualiza os valores locais
watch(() => props.initialFields, (newValue) => {
  if (JSON.stringify(newValue) !== JSON.stringify(fields.value)) {
    fields.value = [...newValue];
  }
}, { deep: true, immediate: true });

const addField = () => {
  const fieldIdKey = props.fieldType === 'tag' ? 'tagId' : 'teamId';
  const fieldErrorKey = props.fieldType === 'tag' ? 'tagError' : 'teamError';
  
  fields.value.unshift({
    [fieldIdKey]: null,
    description: '',
    [fieldErrorKey]: '',
    descriptionError: ''
  });
  
  // Revalida imediatamente após adicionar
  const isValid = validateFields();
  emit('validation', isValid);
};

const updateFields = async (updatedFields) => {
  fields.value = updatedFields;
  
  // Aguarda o próximo ciclo de renderização
  await nextTick();
  
  // Valida e emite o resultado
  const isValid = validateFields();
  emit('validation', isValid);
};

// Função para validar todos os campos
const validateFields = () => {
  // Se não houver campos, é válido (campos são opcionais)
  if (fields.value.length === 0) return true;
  
  const fieldIdKey = props.fieldType === 'tag' ? 'tagId' : 'teamId';
  
  // Se houver campos, todos devem estar preenchidos corretamente
  return fields.value.every(field => {
    return field[fieldIdKey] && field.description && field.description.trim();
  });
};

// Atualiza o estado de validação quando recebe do componente filho
const handleValidation = (isValid) => {
  fieldsValid.value = isValid;
  emit('validation', isValid);
};

// Validação inicial na montagem
onMounted(async () => {
  await nextTick();
  const isValid = validateFields();
  emit('validation', isValid);
});

// Função para solicitar abertura do modal de informação
const requestShowInfo = () => {
  emit('show-info');
};
</script>

<template>
  <div class="space-y-6">
    <div class="space-y-4">
      <!-- Cabeçalho com título e botões -->
      <div class="flex items-center justify-between">
        <h3 class="text-base font-medium text-n-slate-12 flex items-center">
          <span :class="fieldType === 'tag' ? 'i-lucide-tag' : 'i-lucide-users'" class="mr-2"></span>
          {{ fieldType === 'tag' ? 'Configuração de Etiquetas' : 'Atribuição de Times' }}
          <!-- Botão de informação -->
          <button
            @click="requestShowInfo"
            class="ml-2 p-1 rounded-full hover:bg-n-slate-3 transition-colors"
            :title="`Informações sobre ${fieldType === 'tag' ? 'etiquetas' : 'times'}`"
          >
            <svg class="w-4 h-4 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </button>
        </h3>
        <Button
          :label="addButtonLabel"
          color="blue"
          variant="outline"
          icon="plus"
          size="sm"
          @click="addField"
        />
      </div>
      
      <!-- Lista de campos -->
      <component
        :is="childComponent"
        v-if="fields.length > 0"
        :items="fields"
        @update="updateFields"
        @validation="handleValidation"
      />
      
      <!-- Mensagem quando não há campos -->
      <div v-else class="text-sm text-n-slate-11 italic bg-n-slate-2 dark:bg-n-slate-12/5 rounded-lg p-4 border border-n-slate-5 dark:border-n-slate-10/20">
        <div class="flex items-center gap-2">
          <span class="i-lucide-info w-4 h-4 text-n-slate-9"></span>
          <span>
            {{ props.fieldType === 'tag' 
              ? 'Nenhuma etiqueta definida. Clique em "Adicionar Configuração de Etiqueta" para configurar as etiquetas que o agente deve aplicar.'
              : 'Nenhum time definido. Clique em "Adicionar Configuração de Time" para configurar os times para atribuição de conversas.' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>